import { useState, useRef, useEffect } from 'react';
import {
  Copy,
  Edit2,
  Trash2,
  Clock,
  Users,
  CheckCircle,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ListTemplate } from '@/types/lists';
import { cn } from '@/utils/cn';

interface TemplateCardProps {
  template: ListTemplate;
  onClone: (template: ListTemplate) => void;
  onEdit: (template: ListTemplate) => void;
  onDelete: (template: ListTemplate) => void;
  onClick?: (template: ListTemplate) => void;
  className?: string;
}

export function TemplateCard({
  template,
  onClone,
  onEdit,
  onDelete,
  onClick,
  className
}: TemplateCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Card
      className={cn(
        "group cursor-pointer transition-all duration-200 hover:shadow-md relative",
        className
      )}
      onClick={() => onClick?.(template)}
    >
      {/* Color indicator */}
      {template.color && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
          style={{ backgroundColor: template.color }}
        />
      )}

      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">
                {template.title}
              </h3>
              <Badge
                variant="outline"
                className="text-xs bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700"
              >
                {template.category}
              </Badge>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2">
              {template.description}
            </p>
          </div>

          {/* Actions Menu */}
          <div className="relative" ref={menuRef}>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {showMenu && (
              <div className="absolute right-0 top-8 bg-card border border-border rounded-md shadow-lg z-10 min-w-[140px] py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClone(template);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Use Template
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(template);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(template);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent text-red-600 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            <span>{template.items.length} items</span>
          </div>

          {template.estimatedHours && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{template.estimatedHours}h</span>
            </div>
          )}

          <div className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span>{template.usageCount} uses</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
